import React, { useEffect } from 'react';
import StarRating from './StarRating';
import { trackScorePopup } from '../utils/analytics';
import { getScore, getTotalStars, getDayScore, getDayStars } from '../utils/completionStorage';
import { sound } from '../utils/sound';

interface ScoreBreakdown {
  relevance: number;
  correctness: number;
  depth: number;
  clarity: number;
  promptcraft: number;
}

interface Feedback {
  improvements?: string[];
  strengths?: string[];
  summary?: string;
}

interface BonusData {
  totalPoints: number;
  stars?: number;
  scoreBreakdown?: ScoreBreakdown;
  scoreDelta?: number;
  addedPoints?: number; // The actual points added to total score
  isImprovement?: boolean; // Whether this is an improvement over previous submission
  previousBreakdown?: ScoreBreakdown | null; // Previous breakdown for comparison
  feedback?: Feedback;
}

interface ScorePopupProps {
  bonusData: BonusData;
  day: number;
  today: boolean;
  onContinue: () => void;
  isClosing?: boolean;
  isFeedbackView?: boolean; // true if viewing previous feedback, false if newly received
}

const ScorePopup: React.FC<ScorePopupProps> = ({ bonusData, day, today, onContinue, isClosing = false, isFeedbackView = false }) => {
  // Track score popup display and play confetti sound on mount
  useEffect(() => {
    // Play confetti sound if this is a new score (not feedback view) and points were added
    if (!isFeedbackView && bonusData.addedPoints && bonusData.addedPoints > 0) {
      sound.confetti();
    }

    trackScorePopup({
      day,
      today,
      score: bonusData.totalPoints,
      stars: bonusData.stars || 0,
      best_day_score: getDayScore(day),
      best_day_stars: getDayStars(day),
      total_score: getScore(),
      total_stars: getTotalStars(),
      isFeedbackView
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleContinueClick();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onContinue]);

  const handleContinueClick = () => {
    sound.clickClose();
    onContinue();
  };

  return (
    <>
      <div className={`score-popup-overlay ${isClosing ? 'closing' : ''}`} />
      <div className={`score-popup-wrapper ${isClosing ? 'closing' : ''}`}>
      <div className={`score-popup-content-wrapper ${isClosing ? 'closing' : ''}`}>
        <div className="score-popup-content">
          {bonusData.stars !== undefined && bonusData.stars > 0 && (
            <div className="score-popup-stars">
              <StarRating stars={bonusData.stars} animate={true} size="xlarge" useSvg={false} />
            </div>
          )}
          <div className="score-popup-body">
            <div className="score-popup-points">
              +{bonusData.totalPoints.toLocaleString()}
            </div>
            {bonusData.scoreBreakdown && (
              <div className="score-breakdown-table">
                <div className="score-column">
                  <div className="score-column-label">Relevance</div>
                  <div className="score-column-value">{bonusData.scoreBreakdown.relevance}%</div>
                  {bonusData.previousBreakdown && bonusData.scoreBreakdown.relevance > bonusData.previousBreakdown.relevance && (
                    <div className="score-column-badge">IMPROVED</div>
                  )}
                </div>
                <div className="score-column">
                  <div className="score-column-label">Correctness</div>
                  <div className="score-column-value">{bonusData.scoreBreakdown.correctness}%</div>
                  {bonusData.previousBreakdown && bonusData.scoreBreakdown.correctness > bonusData.previousBreakdown.correctness && (
                    <div className="score-column-badge">IMPROVED</div>
                  )}
                </div>
                <div className="score-column">
                  <div className="score-column-label">Depth</div>
                  <div className="score-column-value">{bonusData.scoreBreakdown.depth}%</div>
                  {bonusData.previousBreakdown && bonusData.scoreBreakdown.depth > bonusData.previousBreakdown.depth && (
                    <div className="score-column-badge">IMPROVED</div>
                  )}
                </div>
                <div className="score-column">
                  <div className="score-column-label">Clarity</div>
                  <div className="score-column-value">{bonusData.scoreBreakdown.clarity}%</div>
                  {bonusData.previousBreakdown && bonusData.scoreBreakdown.clarity > bonusData.previousBreakdown.clarity && (
                    <div className="score-column-badge">IMPROVED</div>
                  )}
                </div>
                <div className="score-column">
                  <div className="score-column-label">Promptcraft</div>
                  <div className="score-column-value">{bonusData.scoreBreakdown.promptcraft}%</div>
                  {bonusData.previousBreakdown && bonusData.scoreBreakdown.promptcraft > bonusData.previousBreakdown.promptcraft && (
                    <div className="score-column-badge">IMPROVED</div>
                  )}
                </div>
              </div>
            )}
            {bonusData.feedback && (
              <div className="feedback-section">
                {bonusData.feedback.summary && (
                  <div className={`feedback-summary ${bonusData.stars === 0 ? 'incomplete' : 'complete'}`}>
                    {bonusData.feedback.summary}
                  </div>
                )}
                {bonusData.feedback.strengths && bonusData.feedback.strengths.length > 0 && (
                  <div className="feedback-group">
                    <div className="feedback-title">Strengths</div>
                    <ul className="feedback-list">
                      {bonusData.feedback.strengths.map((strength, index) => (
                        <li key={index}>{strength}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {bonusData.feedback.improvements && bonusData.feedback.improvements.length > 0 && (
                  <div className="feedback-group">
                    <div className="feedback-title">Areas for Improvement</div>
                    <ul className="feedback-list">
                      {bonusData.feedback.improvements.map((improvement, index) => (
                        <li key={index}>{improvement}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <button className="score-popup-continue" onClick={handleContinueClick}>
        Continue
      </button>
      </div>
    </>
  );
};

export default ScorePopup;
